import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    subscribeNewsletter: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  
  const containerRef = useRef();
  const formRef = useRef();
  const logoRef = useRef();
  const videoRef = useRef();
  
  const { signup, currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if already logged in
    if (currentUser) {
      navigate('/');
      return;
    }

    // Entrance animations
    const tl = gsap.timeline();
    
    // Video section animation
    tl.fromTo(videoRef.current,
      { x: -100, opacity: 0 },
      { x: 0, opacity: 1, duration: 1.2, ease: "power3.out" }
    )
    .fromTo(logoRef.current,
      { x: 50, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      "-=0.8"
    )
    .fromTo(formRef.current,
      { x: 50, opacity: 0 },
      { x: 0, opacity: 1, duration: 1, ease: "power3.out" },
      "-=0.6"
    );
  }, [currentUser, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    if (!formData.agreeToTerms) {
      setError('Please agree to the Terms & Conditions');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await signup(formData.email, formData.password, formData.firstName, formData.lastName);
      // Redirect to home page on successful signup
      navigate('/');
    } catch (error) {
      console.error('Signup error:', error);
      // Handle different error types
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email/password accounts are not enabled.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SignupContainer ref={containerRef}>
      <VideoSection ref={videoRef}>
        <VideoBackground>
          <video autoPlay muted loop playsInline>
            <source src="/video/nike login.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </VideoBackground>
        <VideoOverlay>
        </VideoOverlay>
      </VideoSection>

      <FormSection>
        <SignupContent>
          <LogoSection ref={logoRef}>
            <Logo>NIKE</Logo>
            <Tagline>Just Do It</Tagline>
          </LogoSection>

          <SignupForm ref={formRef} onSubmit={handleSubmit}>
            <FormTitle>Join Nike</FormTitle>
            <FormSubtitle>Create your Nike account and unlock exclusive access</FormSubtitle>
            
            {error && <ErrorMessage>{error}</ErrorMessage>}

            <InputRow>
              <InputGroup>
                <InputLabel>First Name</InputLabel>
                <InputField
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                  required
                />
              </InputGroup>
              <InputGroup>
                <InputLabel>Last Name</InputLabel>
                <InputField
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                  required
                />
              </InputGroup>
            </InputRow>

            <InputGroup>
              <InputLabel>Email Address</InputLabel>
              <InputField
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                required
              />
            </InputGroup>

            <InputGroup>
              <InputLabel>Password</InputLabel>
              <PasswordWrapper>
                <InputField
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Create a password"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </PasswordToggle>
              </PasswordWrapper>
            </InputGroup>

            <InputGroup>
              <InputLabel>Confirm Password</InputLabel>
              <PasswordWrapper>
                <InputField
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm your password"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </PasswordToggle>
              </PasswordWrapper>
            </InputGroup>

            <FormOptions>
              <CheckboxGroup>
                <Checkbox 
                  type="checkbox" 
                  id="agreeTerms" 
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  required
                />
                <CheckboxLabel htmlFor="agreeTerms">
                  I agree to Nike's <TermsLink href="#">Terms & Conditions</TermsLink> and <TermsLink href="#">Privacy Policy</TermsLink>
                </CheckboxLabel>
              </CheckboxGroup>
              
              <CheckboxGroup>
                <Checkbox 
                  type="checkbox" 
                  id="newsletter" 
                  name="subscribeNewsletter"
                  checked={formData.subscribeNewsletter}
                  onChange={handleInputChange}
                />
                <CheckboxLabel htmlFor="newsletter">
                  Send me Nike emails with product updates and special offers
                </CheckboxLabel>
              </CheckboxGroup>
            </FormOptions>

            <SignupButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </SignupButton>

            <Divider>
              <DividerLine />
              <DividerText>or</DividerText>
              <DividerLine />
            </Divider>

            <SocialButtons>
              <GoogleButton type="button">
                <GoogleIcon>
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </GoogleIcon>
                Sign up with Google
              </GoogleButton>
              <AppleButton type="button">
                <AppleIcon>
                  <svg viewBox="0 0 24 24" width="18" height="18">
                    <path fill="#fff" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </AppleIcon>
                Sign up with Apple
              </AppleButton>
            </SocialButtons>

            <LoginPrompt>
              Already have an account? <LoginLink to="/login">Sign in</LoginLink>
            </LoginPrompt>
          </SignupForm>
        </SignupContent>
      </FormSection>
    </SignupContainer>
  );
};

export default Signup;

const SignupContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: #f8f9fa;
  padding: 20px;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
`;

const VideoSection = styled.div`
  flex: 1;
  position: relative;
  min-height: calc(100vh - 40px);
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  background: #000;
  
  @media (max-width: 768px) {
    min-height: calc(35vh - 16px);
    flex: none;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }
`;

const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  border-radius: 20px;
  overflow: hidden;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 20px;
  }
  
  @media (max-width: 768px) {
    border-radius: 16px;
    
    video {
      border-radius: 16px;
    }
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
  z-index: 2;
  border-radius: 20px;
  
  @media (max-width: 768px) {
    border-radius: 16px;
  }
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  padding: 40px 20px;
  min-height: calc(100vh - 40px);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  
  @media (max-width: 768px) {
    min-height: calc(65vh - 16px);
    flex: none;
    padding: 32px 20px;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  }
`;

const SignupContent = styled.div`
  width: 100%;
  max-width: 480px;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    padding: 0 16px;
  }
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const Logo = styled.h1`
  font-family: 'Anton', sans-serif;
  font-size: 32px;
  color: #111;
  margin: 0 0 4px 0;
  letter-spacing: 2px;
`;

const Tagline = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const SignupForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111;
  margin: 0 0 8px 0;
  text-align: center;
`;

const FormSubtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 32px 0;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  border: 1px solid #fed7d7;
  text-align: center;
`;

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
  flex: 1;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const InputField = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: #fff;
  
  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const PasswordWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }
  
  &:active {
    background: rgba(0, 0, 0, 0.08);
    transform: translateY(-50%) scale(0.95);
  }
  
  svg {
    transition: all 0.2s ease;
  }
`;

const FormOptions = styled.div`
  margin-bottom: 24px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #111;
  margin-top: 2px;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: #666;
  cursor: pointer;
  line-height: 1.4;
`;

const TermsLink = styled.a`
  color: #111;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignupButton = styled.button`
  width: 100%;
  background: #111;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
  
  &:hover:not(:disabled) {
    background: #000;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e1e5e9;
`;

const DividerText = styled.span`
  padding: 0 16px;
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SocialButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const GoogleButton = styled.button`
  width: 100%;
  background: #ffffff;
  color: #3c4043;
  border: 1px solid #dadce0;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  
  &:hover {
    background: #f8f9fa;
    border-color: #c1c7cd;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    background: #f1f3f4;
    border-color: #c1c7cd;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const AppleButton = styled.button`
  width: 100%;
  background: #000000;
  color: #ffffff;
  border: 1px solid #000000;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  &:hover {
    background: #1a1a1a;
    border-color: #1a1a1a;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    background: #0a0a0a;
    border-color: #0a0a0a;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }
`;

const GoogleIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AppleIcon = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginPrompt = styled.p`
  text-align: center;
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const LoginLink = styled(Link)`
  color: #111;
  text-decoration: none;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;
